import { useState, type FormEvent } from "react";

/**
 * "Get launch news": the owner-approved box (listmonk/CONSENT.md, 2026-10-17).
 * It posts to the news gate on lists.clearmakes.com, which sends one confirm
 * email; nobody is added until they press its button. `website` is a hidden
 * field only robots fill in. Keep the words identical to CONSENT.md.
 * clearmakes.com signs people up for all three products' news ("clearmakes").
 * Explicit colours only: older pages wrap the footer in `.legacy`.
 */
const GATE = "https://lists.clearmakes.com/api/news/subscribe";

export const NewsBox = ({ product = "clearmakes" }: { product?: "meet" | "inbox" | "agents" | "clearmakes" }) => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setState("error");
      setMessage("That email doesn't look right.");
      return;
    }
    setState("sending");
    try {
      const r = await fetch(GATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), product, website }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setState("sent");
        setMessage("Almost done. Check your inbox and click the link to confirm.");
      } else {
        setState("error");
        setMessage(d.message || (r.status === 429 ? "Too many tries. Please try again later." : "That didn't work. Please try again."));
      }
    } catch {
      setState("error");
      setMessage("That didn't work. Please try again.");
    }
  };

  return (
    <div className="mb-14 flex flex-col gap-6 border-b border-[hsl(216_30%_86%)] pb-12 lg:flex-row lg:items-center lg:justify-between">
      <div className="max-w-md">
        <h2 className="text-[1.35rem] font-bold tracking-[-0.02em] text-[hsl(222_47%_7%)]">
          Want to hear when something new is ready?
        </h2>
        <p className="mt-2 text-[15px] leading-snug text-[hsl(216_12%_32%)]">
          One short email when there's a new feature or launch. At most twice a month.
        </p>
      </div>
      {state === "sent" ? (
        <p className="text-[15px] font-medium text-[hsl(222_47%_7%)]" role="status">{message}</p>
      ) : (
        <form onSubmit={submit} className="w-full max-w-md" noValidate>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="email"
              required
              autoComplete="email"
              aria-label="Your email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 flex-1 rounded-xl border border-[hsl(216_30%_80%)] bg-white px-4 text-[15px] text-[hsl(222_47%_7%)] placeholder:text-[hsl(216_12%_55%)] focus:border-[hsl(216_84%_52%)] focus:outline-none"
            />
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="absolute -left-[9999px] h-px w-px opacity-0"
            />
            <button
              type="submit"
              disabled={state === "sending"}
              className="h-12 rounded-xl bg-[hsl(0_0%_4%)] px-6 text-[15px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              Get launch news
            </button>
          </div>
          {state === "error" && <p className="mt-2 text-[14px] text-[hsl(0_72%_45%)]" role="alert">{message}</p>}
          <p className="mt-2 text-[13px] text-[hsl(216_12%_38%)]">
            We'll send one email to confirm. Unsubscribe anytime.{" "}
            <a href="/privacy" className="underline underline-offset-2 hover:text-[hsl(222_47%_7%)]">Privacy Policy</a>
          </p>
        </form>
      )}
    </div>
  );
};
